import { FC } from 'react'
import { Box, Typography, colors } from '@mui/material'
import PersonAdd from '@mui/icons-material/PersonAdd'
import SupportAgent from '@mui/icons-material/SupportAgent'
import InfoOutlined from '@mui/icons-material/InfoOutlined'

import MessageActions from './MessageActions'
import { BffCrmPersonChatRetrieveResponse } from '@fireflyhealth/api'

type ChatCommunication = Extract<
  BffCrmPersonChatRetrieveResponse['communications']['results'][number]['content_object'],
  { kind: 'chat' }
>

interface ChatMessageEventProps {
  message: Pick<
    ChatCommunication,
    | 'kind'
    | 'id'
    | 'uid'
    | 'message_tags'
    | 'is_urgent_message'
    | 'event'
    | 'text'
    | 'sent_at'
    | 'attachment'
    | 'file_attachment'
  > & {
    sender: Pick<ChatCommunication['sender'], 'id' | 'avatar_url' | 'full_name_used'>
    case_relations: {
      case: Pick<ChatCommunication['case_relations'][number]['case'], 'id'>
    }[]
  }
  person: Pick<BffCrmPersonChatRetrieveResponse['person'], 'id' | 'user'>
  threadUid: string | null
}

// Keyed by the generated event enum rather than `string`, so adding a value to the
// backend's `EventTypes` fails to compile here until someone picks a glyph for it —
// a `Record<string, …>` would silently fall through to the default instead.
type ChatEventKind = NonNullable<ChatCommunication['event']>

// Each glyph asserts what happened, so an event this build cannot identify falls back
// to one that asserts nothing. `PersonAdd` would claim someone joined the chat, which
// is only true of an arrival.
const ICON_BY_EVENT: Record<ChatEventKind, typeof PersonAdd> = {
  arrival: PersonAdd,
  escalated_to_human: SupportAgent,
}

const ChatMessageEvent: FC<ChatMessageEventProps> = ({ message, person, threadUid }) => {
  // The fallback still earns its place: a newer backend can send a value outside this
  // build's enum, which no type can rule out.
  const EventIcon = (message.event && ICON_BY_EVENT[message.event]) || InfoOutlined

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: colors.blueGrey[50],
              color: colors.blueGrey[800],
              py: 2,
              px: 3,
              borderRadius: 1,
              '&:hover .message-actions-hover-control': {
                opacity: '1 !important',
              },
            }}
          >
            <EventIcon color="inherit" fontSize="small" />
            <Box sx={{ ml: 1 }}>
              <Typography variant="caption">{message.text}</Typography>
            </Box>
            {/* Allows deleting the event, but won't show the usual casing controls */}
            <MessageActions
              isLastMessage={true}
              message={message}
              person={person}
              setIsLoading={() => null}
              threadUid={threadUid}
              canCase={false}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default ChatMessageEvent
